import { Rarity, Stats } from '../sim/types'

export interface ItemDef {
  id: string
  name: string
  icon: string
  rarity: Rarity
  maxLevel: number
  describe(level: number): string
  /** Mutates the stats block. Called once per owned level during recompute. */
  apply(stats: Stats, level: number): void
  /** Trigger hook + handler kind, fired in dealDamage/killEnemy (see applyProcs). */
  proc?: { hook: 'hit' | 'kill' | 'crit'; kind: string }
}

export const ITEM_LIST: ItemDef[] = [
  {
    id: 'protein_shake',
    name: 'Protein Shake',
    icon: '🥤',
    rarity: 'common',
    maxLevel: 5,
    describe: (l) => `+12% damage per level (currently +${12 * l}%).`,
    apply: (s, l) => {
      s.damage += 0.12 * l
    },
  },
  {
    id: 'energy_drink',
    name: 'Energy Drink',
    icon: '⚡',
    rarity: 'common',
    maxLevel: 5,
    describe: (l) => `+10% attack speed per level (currently +${10 * l}%).`,
    apply: (s, l) => {
      s.attackSpeed += 0.1 * l
    },
  },
  {
    id: 'running_shoes',
    name: 'Running Shoes',
    icon: '👟',
    rarity: 'common',
    maxLevel: 5,
    describe: (l) => `+8% move speed per level (currently +${8 * l}%).`,
    apply: (s, l) => {
      s.moveSpeed *= 1 + 0.08 * l
    },
  },
  {
    id: 'big_lunch',
    name: 'Big Lunch',
    icon: '🍔',
    rarity: 'common',
    maxLevel: 5,
    describe: (l) => `+20 max HP per level (currently +${20 * l}).`,
    apply: (s, l) => {
      s.maxHp += 20 * l
    },
  },
  {
    id: 'magnet',
    name: 'Lodestone',
    icon: '🧲',
    rarity: 'common',
    maxLevel: 4,
    describe: (l) => `+45% pickup range per level (currently +${45 * l}%).`,
    apply: (s, l) => {
      s.pickupRange *= 1 + 0.45 * l
    },  },
  {
    id: 'lucky_clover',
    name: 'Lucky Clover',
    icon: '🍀',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+0.6 luck per level — better rarities and procs.`,
    apply: (s, l) => {
      s.luck += 0.6 * l
    },
  },
  {
    id: 'spyglass',
    name: 'Spyglass',
    icon: '🔭',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+8% crit chance and +0.15 crit damage per level.`,
    apply: (s, l) => {
      s.critChance += 0.08 * l
      s.critMult += 0.15 * l
    },
  },
  {
    id: 'wide_lens',
    name: 'Wide Lens',
    icon: '🔎',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+12% area per level (currently +${12 * l}%).`,
    apply: (s, l) => {
      s.area *= 1 + 0.12 * l
    },
  },
  {
    id: 'spring_coil',
    name: 'Spring Coil',
    icon: '🪤',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+15% projectile speed and +10% duration per level.`,
    apply: (s, l) => {
      s.projectileSpeed *= 1 + 0.15 * l
      s.duration *= 1 + 0.1 * l
    },
  },
  {
    id: 'cursed_doll',
    name: 'Cursed Doll',
    icon: '🎎',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+25% damage per level, but -10% max HP per level.`,
    apply: (s, l) => {
      s.damage += 0.25 * l
      s.maxHp *= 1 - 0.1 * l
    },  },
  {
    id: 'leech_ring',
    name: 'Leech Ring',
    icon: '💍',
    rarity: 'rare',
    maxLevel: 4,
    describe: (l) => `Heal for ${(2 * l).toFixed(0)}% of damage dealt.`,
    apply: (s, l) => {
      s.lifesteal += 0.02 * l
    },
  },
  {
    id: 'iron_plate',
    name: 'Iron Plate',
    icon: '🛡️',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+4 armor per level — flat damage reduction.`,
    apply: (s, l) => {
      s.armor += 4 * l
    },
  },
  {
    id: 'ghost_cloak',
    name: 'Ghost Cloak',
    icon: '👻',
    rarity: 'rare',
    maxLevel: 5,
    describe: (l) => `+6% dodge per level — chance to ignore a hit entirely.`,
    apply: (s, l) => {
      s.dodge += 0.06 * l
    },
  },
  {
    id: 'thorn_mail',
    name: 'Thorn Mail',
    icon: '🌵',
    rarity: 'rare',
    maxLevel: 4,
    describe: (l) => `Reflect ${(25 * l).toFixed(0)}% of contact damage back as thorns.`,
    apply: (s, l) => {
      s.thorns += 0.25 * l
    },  },
  {
    id: 'piggy_bank',
    name: 'Piggy Bank',
    icon: '🐷',
    rarity: 'uncommon',
    maxLevel: 4,
    describe: (l) => `+25% gold gain per level.`,
    apply: (s, l) => {
      s.goldGain *= 1 + 0.25 * l
    },  },
  {
    id: 'graduation_cap',
    name: 'Graduation Cap',
    icon: '🎓',
    rarity: 'uncommon',
    maxLevel: 4,
    describe: (l) => `+20% XP gain per level.`,
    apply: (s, l) => {
      s.xpGain *= 1 + 0.2 * l
    },
  },
  {
    id: 'phoenix_feather',
    name: 'Phoenix Feather',
    icon: '🪶',
    rarity: 'legendary',
    maxLevel: 2,
    describe: (l) => `Revive once per level with 50% HP when you die.`,
    apply: (s, l) => {
      s.revives += l
    },  },
  {
    id: 'heart_locket',
    name: 'Heart Locket',
    icon: '❤️',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+1.2 HP regen per second per level.`,
    apply: (s, l) => {
      s.hpRegen += 1.2 * l
    },
  },

  // ===== Evolution catalyst items (also useful passives) =====
  {
    id: 'gale_charm',
    name: 'Gale Charm',
    icon: '🍃',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+10% projectile speed & +5% knockback per level. (Evolves Boomeraxe)`,
    apply: (s, l) => {
      s.projectileSpeed *= 1 + 0.1 * l
      s.knockback *= 1 + 0.05 * l
    },
  },
  {
    id: 'hot_sauce',
    name: 'Hot Sauce',
    icon: '🌶️',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+10% damage & +6% attack speed per level. (Evolves Revolver)`,
    apply: (s, l) => {
      s.damage += 0.1 * l
      s.attackSpeed += 0.06 * l
    },
  },
  {
    id: 'gyroscope',
    name: 'Gyroscope',
    icon: '🎯',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+1 projectile every level & +6% area. (Evolves Halo)`,
    apply: (s, l) => {
      s.projectileCount += l
      s.area *= 1 + 0.06 * l
    },
  },
  {
    id: 'frozen_heart',
    name: 'Frozen Heart',
    icon: '🧊',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+10% area & +4 armor per level. Frozen foes SHATTER when struck. (Evolves Frost Pulse)`,
    apply: (s, l) => {
      s.area *= 1 + 0.1 * l
      s.armor += 4 * l
    },
  },
  {
    id: 'ember_core',
    name: 'Ember Core',
    icon: '🔆',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+15% damage, +8% area & +12% damage to afflicted foes per level. Burning foes COMBUST on death (fire nova). (Evolves Meteor)`,
    apply: (s, l) => {
      s.damage += 0.15 * l
      s.area *= 1 + 0.08 * l
      s.statusDamage += 0.12 * l
    },
  },
  {
    id: 'capacitor',
    name: 'Capacitor',
    icon: '🔋',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+8% attack speed per level; +1 projectile at level 2. Crits ARC lightning to a nearby foe. (Evolves Zapper)`,
    apply: (s, l) => {
      s.attackSpeed += 0.08 * l
      if (l >= 2) s.projectileCount += 1
    },
  },
  {
    id: 'biohazard',
    name: 'Biohazard',
    icon: '🧪',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+10% damage & +8% area per level. (Evolves Rotsworn)`,
    apply: (s, l) => {
      s.damage += 0.1 * l
      s.area *= 1 + 0.08 * l
    },
  },
  {
    id: 'barbed_wire',
    name: 'Barbed Wire',
    icon: '🩹',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+8% damage & +12% knockback per level. (Evolves Lash)`,
    apply: (s, l) => {
      s.damage += 0.08 * l
      s.knockback *= 1 + 0.12 * l
    },
  },
  {
    id: 'gunpowder',
    name: 'Gunpowder',
    icon: '🧨',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+12% damage & +8% area per level. (Evolves Caltrops)`,
    apply: (s, l) => {
      s.damage += 0.12 * l
      s.area *= 1 + 0.08 * l
    },
  },
  {
    id: 'rubber_ball',
    name: 'Rubber Ball',
    icon: '🎾',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+12% projectile speed & +1 projectile per level. (Evolves Ricochet)`,
    apply: (s, l) => {
      s.projectileSpeed *= 1 + 0.12 * l
      s.projectileCount += l
    },
  },
  {
    id: 'dark_matter',
    name: 'Dark Matter',
    icon: '🌌',
    rarity: 'epic',
    maxLevel: 3,
    describe: (l) => `+10% area & +0.5 luck per level. (Evolves Singularity)`,
    apply: (s, l) => {
      s.area *= 1 + 0.1 * l
      s.luck += 0.5 * l
    },
  },
  {
    id: 'tungsten_rod',
    name: 'Tungsten Rod',
    icon: '🔩',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+12% damage & +12% projectile speed per level. (Evolves Railgun)`,
    apply: (s, l) => {
      s.damage += 0.12 * l
      s.projectileSpeed *= 1 + 0.12 * l
    },
  },
  {
    id: 'fungal_spores',
    name: 'Fungal Spores',
    icon: '🍄',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+10% damage, +12% area & +12% damage to afflicted foes per level. Poisoned deaths spread CONTAGION. (Evolves Spore Cloud)`,
    apply: (s, l) => {
      s.damage += 0.1 * l
      s.area *= 1 + 0.12 * l
      s.statusDamage += 0.12 * l
    },
  },
  {
    id: 'permafrost',
    name: 'Permafrost',
    icon: '🧊',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+10% area & +12% effect duration per level. (Evolves Frost Lance)`,
    apply: (s, l) => {
      s.area *= 1 + 0.1 * l
      s.duration *= 1 + 0.12 * l
    },
  },
  {
    id: 'solar_panel',
    name: 'Solar Panel',
    icon: '🔆',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+12% damage & +8% area per level. (Evolves Solar Flare)`,
    apply: (s, l) => {
      s.damage += 0.12 * l
      s.area *= 1 + 0.08 * l
    },
  },
  {
    id: 'fusion_core',
    name: 'Fusion Core',
    icon: '⚛️',
    rarity: 'epic',
    maxLevel: 3,
    describe: (l) => `+15% damage & +8% area per level. (Evolves Nova Mortar)`,
    apply: (s, l) => {
      s.damage += 0.15 * l
      s.area *= 1 + 0.08 * l
    },
  },
  {
    id: 'crystal_lens',
    name: 'Crystal Lens',
    icon: '💠',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+1 projectile per level & +8% projectile speed. (Evolves Prism Beam)`,
    apply: (s, l) => {
      s.projectileCount += l
      s.projectileSpeed *= 1 + 0.08 * l
    },
  },
  {
    id: 'event_shard',
    name: 'Event Shard',
    icon: '🔷',
    rarity: 'epic',
    maxLevel: 3,
    describe: (l) => `+12% damage & +8% area per level. (Evolves Quasar Orb)`,
    apply: (s, l) => {
      s.damage += 0.12 * l
      s.area *= 1 + 0.08 * l
    },
  },
  {
    id: 'light_amp',
    name: 'Light Amplifier',
    icon: '🔦',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+8% attack speed & +10% projectile speed per level. (Evolves Photon Lance)`,
    apply: (s, l) => {
      s.attackSpeed += 0.08 * l
      s.projectileSpeed *= 1 + 0.1 * l
    },
  },
  {
    id: 'acid_vial',
    name: 'Acid Vial',
    icon: '⚗️',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+10% damage & +10% area per level. (Evolves Acid Spit)`,
    apply: (s, l) => {
      s.damage += 0.1 * l
      s.area *= 1 + 0.1 * l
    },
  },
  {
    id: 'kinetic_cell',
    name: 'Kinetic Cell',
    icon: '🔋',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+8% damage & +15% knockback per level. (Evolves Shock Nova)`,
    apply: (s, l) => {
      s.damage += 0.08 * l
      s.knockback *= 1 + 0.15 * l
    },
  },
  {
    id: 'shard_matrix',
    name: 'Shard Matrix',
    icon: '🔱',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+1 projectile per level & +10% projectile speed. (Evolves Star Shards)`,
    apply: (s, l) => {
      s.projectileCount += l
      s.projectileSpeed *= 1 + 0.1 * l
    },
  },
  {
    id: 'cryo_canister',
    name: 'Cryo Canister',
    icon: '🥶',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+10% area & +12% effect duration per level. (Evolves Cryo Bomb)`,
    apply: (s, l) => {
      s.area *= 1 + 0.1 * l
      s.duration *= 1 + 0.12 * l
    },
  },
  {
    id: 'plasma_cell',
    name: 'Plasma Cell',
    icon: '🔆',
    rarity: 'rare',
    maxLevel: 3,
    describe: (l) => `+12% damage & +6% area per level. (Evolves Plasma Cutter)`,
    apply: (s, l) => {
      s.damage += 0.12 * l
      s.area *= 1 + 0.06 * l
    },
  },

  // ===== Standalone passive items (stat hybrids, EN+IT, no evolution) =====
  {
    id: 'overclock_chip',
    name: 'Overclock Chip',
    icon: '⏱️',
    rarity: 'rare',
    maxLevel: 5,
    describe: (l) => `+7% cooldown reduction per level — all weapons fire faster.`,
    apply: (s, l) => {
      s.cooldownReduction += 0.07 * l
    },
  },
  {
    id: 'targeting_array',
    name: 'Targeting Array',
    icon: '🎯',
    rarity: 'epic',
    maxLevel: 4,
    describe: (l) => `+1 projectile every 2 levels & +8% projectile speed per level.`,
    apply: (s, l) => {
      s.projectileCount += Math.floor(l / 2)
      s.projectileSpeed *= 1 + 0.08 * l
    },
  },
  {
    id: 'recoil_booster',
    name: 'Recoil Booster',
    icon: '💢',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+10% knockback & +6% damage per level.`,
    apply: (s, l) => {
      s.knockback *= 1 + 0.1 * l
      s.damage += 0.06 * l
    },
  },
  {
    id: 'nano_coating',
    name: 'Nano Coating',
    icon: '🧴',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+3 armor & +12 max HP per level.`,
    apply: (s, l) => {
      s.armor += 3 * l
      s.maxHp += 12 * l
    },
  },
  {
    id: 'focusing_array',
    name: 'Focusing Array',
    icon: '📡',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+10% area & +8% damage per level.`,
    apply: (s, l) => {
      s.area *= 1 + 0.1 * l
      s.damage += 0.08 * l
    },
  },
  {
    id: 'adrenal_implant',
    name: 'Adrenal Implant',
    icon: '💉',
    rarity: 'rare',
    maxLevel: 5,
    describe: (l) => `+7% move speed & +8% attack speed per level.`,
    apply: (s, l) => {
      s.moveSpeed *= 1 + 0.07 * l
      s.attackSpeed += 0.08 * l
    },
  },
  {
    id: 'vampiric_fang',
    name: 'Vampiric Fang',
    icon: '🦷',
    rarity: 'epic',
    maxLevel: 4,
    describe: (l) => `Heal ${(2.5 * l).toFixed(1)}% of damage dealt & +6% damage per level.`,
    apply: (s, l) => {
      s.lifesteal += 0.025 * l
      s.damage += 0.06 * l
    },
  },
  {
    id: 'quantum_battery',
    name: 'Quantum Battery',
    icon: '🔋',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+12% effect duration & +8% area per level.`,
    apply: (s, l) => {
      s.duration *= 1 + 0.12 * l
      s.area *= 1 + 0.08 * l
    },
  },
  {
    id: 'star_chart',
    name: 'Star Chart',
    icon: '🗺️',
    rarity: 'uncommon',
    maxLevel: 4,
    describe: (l) => `+0.5 luck & +18% gold gain per level.`,
    apply: (s, l) => {
      s.luck += 0.5 * l
      s.goldGain *= 1 + 0.18 * l
    },  },
  {
    id: 'combat_stims',
    name: 'Combat Stims',
    icon: '⚗️',
    rarity: 'epic',
    maxLevel: 4,
    describe: (l) => `+6% crit chance, +0.12 crit damage & +5% attack speed per level.`,
    apply: (s, l) => {
      s.critChance += 0.06 * l
      s.critMult += 0.12 * l
      s.attackSpeed += 0.05 * l
    },
  },
  {
    id: 'reinforced_hull',
    name: 'Reinforced Hull',
    icon: '🚀',
    rarity: 'rare',
    maxLevel: 5,
    describe: (l) => `+18 max HP, +2 armor & +0.8 HP regen per level.`,
    apply: (s, l) => {
      s.maxHp += 18 * l
      s.armor += 2 * l
      s.hpRegen += 0.8 * l
    },
  },
  {
    id: 'scavenger_drone',
    name: 'Scavenger Drone',
    icon: '🛰️',
    rarity: 'uncommon',
    maxLevel: 4,
    describe: (l) => `+40% pickup range & +15% XP gain per level.`,
    apply: (s, l) => {
      s.pickupRange *= 1 + 0.4 * l
      s.xpGain *= 1 + 0.15 * l
    },  },
  {
    id: 'warp_boots',
    name: 'Warp Boots',
    icon: '🥾',
    rarity: 'rare',
    maxLevel: 5,
    describe: (l) => `+8% move speed & +5% dodge per level.`,
    apply: (s, l) => {
      s.moveSpeed *= 1 + 0.08 * l
      s.dodge += 0.05 * l
    },
  },
  {
    id: 'glass_reactor',
    name: 'Glass Reactor',
    icon: '🔮',
    rarity: 'epic',
    maxLevel: 3,
    describe: (l) => `+30% damage per level, but -8% max HP per level.`,
    apply: (s, l) => {
      s.damage += 0.3 * l
      s.maxHp *= 1 - 0.08 * l
    },  },
  {
    id: 'kinetic_barrier',
    name: 'Kinetic Barrier',
    icon: '🛡️',
    rarity: 'rare',
    maxLevel: 4,
    describe: (l) => `Reflect ${(20 * l).toFixed(0)}% of contact damage & +5% dodge per level.`,
    apply: (s, l) => {
      s.thorns += 0.2 * l
      s.dodge += 0.05 * l
    },  },
  {
    id: 'ion_thrusters',
    name: 'Ion Thrusters',
    icon: '🔥',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+14% projectile speed & +6% attack speed per level.`,
    apply: (s, l) => {
      s.projectileSpeed *= 1 + 0.14 * l
      s.attackSpeed += 0.06 * l
    },
  },
  {
    id: 'gravity_anchor',
    name: 'Gravity Anchor',
    icon: '⚓',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+16 max HP & +14% knockback per level.`,
    apply: (s, l) => {
      s.maxHp += 16 * l
      s.knockback *= 1 + 0.14 * l
    },
  },
  {
    id: 'singularity_core',
    name: 'Singularity Core',
    icon: '🌑',
    rarity: 'legendary',
    maxLevel: 3,
    describe: (l) => `+12% damage, +8% area & +0.4 luck per level.`,
    apply: (s, l) => {
      s.damage += 0.12 * l
      s.area *= 1 + 0.08 * l
      s.luck += 0.4 * l
    },
  },

  // ===== Proc / trigger items (on-hit / on-kill / on-crit) =====
  {
    id: 'volatile_rounds',
    name: 'Volatile Rounds',
    icon: '💥',
    rarity: 'rare',
    maxLevel: 4,
    describe: (l) => `${6 + 2 * l}% chance on hit to trigger a small explosion. +4% damage per level.`,
    apply: (s, l) => {
      s.damage += 0.04 * l
    },
    proc: { hook: 'hit', kind: 'volatile' },
  },
  {
    id: 'static_discharge',
    name: 'Static Discharge',
    icon: '⚡',
    rarity: 'rare',
    maxLevel: 4,
    describe: (l) => `Crits arc lightning to ${1 + l} nearby foes. +4% crit chance per level.`,
    apply: (s, l) => {
      s.critChance += 0.04 * l
    },
    proc: { hook: 'crit', kind: 'static' },
  },
  {
    id: 'soul_harvest',
    name: 'Soul Harvest',
    icon: '👻',
    rarity: 'epic',
    maxLevel: 3,
    describe: (l) => `Every ${Math.max(8, 26 - 6 * l)} kills launches a homing soul-nuke. +10% XP per level.`,
    apply: (s, l) => {
      s.xpGain *= 1 + 0.1 * l
    },
    proc: { hook: 'kill', kind: 'harvest' },
  },
  {
    id: 'critical_mass',
    name: 'Critical Mass',
    icon: '🌡️',
    rarity: 'epic',
    maxLevel: 4,
    describe: (l) => `Each crit stacks +2% damage (max +30%), reset after 4s without a crit. +0.1 crit dmg per level.`,
    apply: (s, l) => {
      s.critMult += 0.1 * l
    },
    proc: { hook: 'crit', kind: 'critmass' },
  },
  {
    id: 'incinerator',
    name: 'Incinerator',
    icon: '🔥',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `${8 + 3 * l}% chance on hit to set the target ablaze. +5% damage per level.`,
    apply: (s, l) => {
      s.damage += 0.05 * l
    },
    proc: { hook: 'hit', kind: 'incinerate' },
  },
  {
    id: 'reapers_toll',
    name: "Reaper's Toll",
    icon: '☠️',
    rarity: 'rare',
    maxLevel: 4,
    describe: (l) => `${8 + 2 * l}% chance on kill to heal and burst nearby foes. +1.5% lifesteal per level.`,
    apply: (s, l) => {
      s.lifesteal += 0.015 * l
    },
    proc: { hook: 'kill', kind: 'reaper' },
  },

  // ===== v11 content-fill: regenerating-shield defensive line (new maxShield stat) =====
  {
    id: 'aegis_capacitor',
    name: 'Aegis Capacitor',
    icon: '🛡️',
    rarity: 'rare',
    maxLevel: 5,
    describe: (l) => `+20 shield & +2 armor per level — shield refills out of the line of fire.`,
    apply: (s, l) => {
      s.maxShield += 20 * l
      s.armor += 2 * l
    },
  },
  {
    id: 'barrier_field',
    name: 'Barrier Field',
    icon: '🔵',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+16 shield & +4% dodge per level.`,
    apply: (s, l) => {
      s.maxShield += 16 * l
      s.dodge += 0.04 * l
    },
  },
  {
    id: 'deflector_array',
    name: 'Deflector Array',
    icon: '🛰️',
    rarity: 'rare',
    maxLevel: 4,
    describe: (l) => `+18 shield & reflect ${(15 * l).toFixed(0)}% of contact damage per level.`,
    apply: (s, l) => {
      s.maxShield += 18 * l
      s.thorns += 0.15 * l
    },
  },
  {
    id: 'overshield_core',
    name: 'Overshield Core',
    icon: '💠',
    rarity: 'epic',
    maxLevel: 3,
    describe: (l) => `+40 shield per level, but -5% move speed per level.`,
    apply: (s, l) => {
      s.maxShield += 40 * l
      s.moveSpeed *= 1 - 0.05 * l
    },
  },
  {
    id: 'cryo_plating',
    name: 'Cryo Plating',
    icon: '🧊',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+12 shield, +2 armor & +8% effect duration per level.`,
    apply: (s, l) => {
      s.maxShield += 12 * l
      s.armor += 2 * l
      s.duration *= 1 + 0.08 * l
    },
  },

  // ===== v11 content-fill: stat-hybrid passives (cover combos the pool was missing) =====
  {
    id: 'titan_serum',
    name: 'Titan Serum',
    icon: '💉',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+22 max HP & +6% damage per level.`,
    apply: (s, l) => {
      s.maxHp += 22 * l
      s.damage += 0.06 * l
    },
  },
  {
    id: 'berserker_chip',
    name: 'Berserker Chip',
    icon: '🩸',
    rarity: 'rare',
    maxLevel: 5,
    describe: (l) => `+9% damage & +7% attack speed per level, but -2 armor per level.`,
    apply: (s, l) => {
      s.damage += 0.09 * l
      s.attackSpeed += 0.07 * l
      s.armor -= 2 * l
    },
  },
  {
    id: 'predator_optics',
    name: 'Predator Optics',
    icon: '🔭',
    rarity: 'epic',
    maxLevel: 4,
    describe: (l) => `+6% crit chance & +0.18 crit damage per level.`,
    apply: (s, l) => {
      s.critChance += 0.06 * l
      s.critMult += 0.18 * l
    },
  },
  {
    id: 'siege_battery',
    name: 'Siege Battery',
    icon: '💥',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+10% area & +12% knockback per level.`,
    apply: (s, l) => {
      s.area *= 1 + 0.1 * l
      s.knockback *= 1 + 0.12 * l
    },
  },
  {
    id: 'arc_reactor',
    name: 'Arc Reactor',
    icon: '🔋',
    rarity: 'rare',
    maxLevel: 5,
    describe: (l) => `+8% attack speed & +1 HP regen per second per level.`,
    apply: (s, l) => {
      s.attackSpeed += 0.08 * l
      s.hpRegen += 1.0 * l
    },
  },
  {
    id: 'blood_chalice',
    name: 'Blood Chalice',
    icon: '🍷',
    rarity: 'rare',
    maxLevel: 4,
    describe: (l) => `Heal ${(2 * l).toFixed(0)}% of damage dealt & +16 max HP per level.`,
    apply: (s, l) => {
      s.lifesteal += 0.02 * l
      s.maxHp += 16 * l
    },
  },
  {
    id: 'windrunner_cloak',
    name: 'Windrunner Cloak',
    icon: '🌬️',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+7% move speed & +6% attack speed per level.`,
    apply: (s, l) => {
      s.moveSpeed *= 1 + 0.07 * l
      s.attackSpeed += 0.06 * l
    },
  },
  {
    id: 'fortune_idol',
    name: 'Fortune Idol',
    icon: '🗿',
    rarity: 'rare',
    maxLevel: 4,
    describe: (l) => `+0.5 luck, +15% gold & +12% XP gain per level.`,
    apply: (s, l) => {
      s.luck += 0.5 * l
      s.goldGain *= 1 + 0.15 * l
      s.xpGain *= 1 + 0.12 * l
    },
  },
  {
    id: 'neural_link',
    name: 'Neural Link',
    icon: '🧠',
    rarity: 'rare',
    maxLevel: 5,
    describe: (l) => `+6% cooldown reduction & +4% crit chance per level.`,
    apply: (s, l) => {
      s.cooldownReduction += 0.06 * l
      s.critChance += 0.04 * l
    },
  },
  {
    id: 'juggernaut_core',
    name: 'Juggernaut Core',
    icon: '⚙️',
    rarity: 'epic',
    maxLevel: 4,
    describe: (l) => `+26 max HP & +3 armor per level, but -4% move speed per level.`,
    apply: (s, l) => {
      s.maxHp += 26 * l
      s.armor += 3 * l
      s.moveSpeed *= 1 - 0.04 * l
    },
  },
  {
    id: 'venom_gland',
    name: 'Venom Gland',
    icon: '🧫',
    rarity: 'rare',
    maxLevel: 5,
    describe: (l) => `+10% damage to afflicted foes & +6% damage per level.`,
    apply: (s, l) => {
      s.statusDamage += 0.1 * l
      s.damage += 0.06 * l
    },
  },
  {
    id: 'glass_lens',
    name: 'Glass Lens',
    icon: '🔮',
    rarity: 'epic',
    maxLevel: 3,
    describe: (l) => `+8% crit chance & +0.2 crit damage per level, but -6% max HP per level.`,
    apply: (s, l) => {
      s.critChance += 0.08 * l
      s.critMult += 0.2 * l
      s.maxHp *= 1 - 0.06 * l
    },
  },
  {
    id: 'swarm_capacitor',
    name: 'Swarm Capacitor',
    icon: '📡',
    rarity: 'epic',
    maxLevel: 4,
    describe: (l) => `+1 projectile every 2 levels & +10% projectile speed per level.`,
    apply: (s, l) => {
      s.projectileCount += Math.floor(l / 2)
      s.projectileSpeed *= 1 + 0.1 * l
    },
  },
  {
    id: 'phoenix_core',
    name: 'Phoenix Core',
    icon: '🪶',
    rarity: 'legendary',
    maxLevel: 1,
    describe: () => `Revive once per run with 50% HP, and +2 HP regen per second.`,
    apply: (s, l) => {
      s.revives += l
      s.hpRegen += 2 * l
    },
  },
  {
    id: 'duelist_gloves',
    name: 'Duelist Gloves',
    icon: '🥊',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `+8% attack speed & +5% crit chance per level.`,
    apply: (s, l) => {
      s.attackSpeed += 0.08 * l
      s.critChance += 0.05 * l
    },
  },
  {
    id: 'gravity_lens',
    name: 'Gravity Lens',
    icon: '🌑',
    rarity: 'rare',
    maxLevel: 4,
    describe: (l) => `+12% area & +10% effect duration per level.`,
    apply: (s, l) => {
      s.area *= 1 + 0.12 * l
      s.duration *= 1 + 0.1 * l
    },
  },
  {
    id: 'prospector_lamp',
    name: 'Prospector Lamp',
    icon: '🏮',
    rarity: 'uncommon',
    maxLevel: 4,
    describe: (l) => `+0.6 luck & +35% pickup range per level.`,
    apply: (s, l) => {
      s.luck += 0.6 * l
      s.pickupRange *= 1 + 0.35 * l
    },
  },
  {
    id: 'warlords_banner',
    name: "Warlord's Banner",
    icon: '🚩',
    rarity: 'epic',
    maxLevel: 4,
    describe: (l) => `+8% damage, +5% attack speed & +6% area per level.`,
    apply: (s, l) => {
      s.damage += 0.08 * l
      s.attackSpeed += 0.05 * l
      s.area *= 1 + 0.06 * l
    },
  },
  {
    id: 'sentinel_plating',
    name: 'Sentinel Plating',
    icon: '🦾',
    rarity: 'rare',
    maxLevel: 5,
    describe: (l) => `+3 armor, +14 shield & +14 max HP per level.`,
    apply: (s, l) => {
      s.armor += 3 * l
      s.maxShield += 14 * l
      s.maxHp += 14 * l
    },
  },

  // ===== v11 content-fill: proc / trigger items (new on-hit / on-kill / on-crit archetypes) =====
  {
    id: 'cryo_rounds',
    name: 'Cryo Rounds',
    icon: '❄️',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `${10 + 3 * l}% chance on hit to chill — slow & briefly freeze. +6% projectile speed per level.`,
    apply: (s, l) => {
      s.projectileSpeed *= 1 + 0.06 * l
    },
    proc: { hook: 'hit', kind: 'frostbite' },
  },
  {
    id: 'soul_siphon',
    name: 'Soul Siphon',
    icon: '🔷',
    rarity: 'rare',
    maxLevel: 4,
    describe: (l) => `On kill, harvest +${1 + l} shield (up to your shield cap). +10 shield per level.`,
    apply: (s, l) => {
      s.maxShield += 10 * l
    },
    proc: { hook: 'kill', kind: 'siphon' },
  },
  {
    id: 'overcharger',
    name: 'Overcharger',
    icon: '⚡',
    rarity: 'epic',
    maxLevel: 4,
    describe: (l) => `${12 + 4 * l}% chance on crit to discharge a chaining arc & burst. +5% crit chance per level.`,
    apply: (s, l) => {
      s.critChance += 0.05 * l
    },
    proc: { hook: 'crit', kind: 'overcharge' },
  },
  {
    id: 'cluster_bombs',
    name: 'Cluster Bombs',
    icon: '🧨',
    rarity: 'rare',
    maxLevel: 4,
    describe: (l) => `${12 + 3 * l}% chance on kill to burst a fiery cluster blast. +5% area per level.`,
    apply: (s, l) => {
      s.area *= 1 + 0.05 * l
    },
    proc: { hook: 'kill', kind: 'detonate' },
  },
  {
    id: 'ember_rounds',
    name: 'Ember Rounds',
    icon: '🔥',
    rarity: 'uncommon',
    maxLevel: 5,
    describe: (l) => `${8 + 3 * l}% chance on hit to set the target ablaze. +5% damage to afflicted foes per level.`,
    apply: (s, l) => {
      s.statusDamage += 0.05 * l
    },
    proc: { hook: 'hit', kind: 'incinerate' },
  },
]

export const ITEMS: Record<string, ItemDef> = {}
for (const it of ITEM_LIST) ITEMS[it.id] = it
